import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/shared/ui";
import { getUserSubscription, type PlanType } from "@/shared/db";
import { supabase } from "@/shared/db";
import { getPlanConfigSafe } from "@/shared/config";
import { toast } from "sonner";
import {
  CreditCard,
  Check,
  X,
  Shield,
  Zap,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { CheckoutForm } from "./CheckoutForm";

// Load Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || "");

function getDisplayPrice(price: number, period: "month" | "year"): number {
  if (period === "year") {
    return price * 12 * 0.9; // 10% discount
  }
  return price;
}

interface CheckoutSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  planType: PlanType | null;
  billingPeriod?: "month" | "year";
  onSuccess?: () => void;
}

interface DiscountState {
  discountApplied: boolean;
  discountPercent: number | null;
  originalAmount: number;
  finalAmount: number;
}

export const CheckoutSidebar: React.FC<CheckoutSidebarProps> = ({
  isOpen,
  onClose,
  planType,
  billingPeriod = "month",
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activating, setActivating] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState<DiscountState | null>(null);
  const checkoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && user && planType) {
      loadCurrentSubscription();
    }
  }, [isOpen, user, planType]);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const loadCurrentSubscription = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const subscription = await getUserSubscription(user.id);
      setCurrentSubscription(subscription);
    } catch (error) {
      console.error("Error loading subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reset when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setSubscriptionId(null);
      setProcessing(false);
      setActivating(false);
      setPromoCode("");
      setDiscount(null);
    }
  }, [isOpen]);

  const initializePayment = async () => {
    if (!user || !planType) return;

    setProcessing(true);

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("No active session");
      }
      const expiresAt = session.expires_at;
      if (expiresAt && expiresAt - Math.floor(Date.now() / 1000) < 60) {
        await supabase.auth.refreshSession();
      }

      const { data: responseData, error } = await supabase.functions.invoke(
        "create-subscription",
        {
          method: "POST",
          body: {
            userId: user.id,
            planType,
            billingPeriod,
            promoCode: promoCode.trim() || undefined,
          },
        },
      );

      if (error) {
        const errorMessage =
          (responseData &&
            typeof responseData === "object" &&
            (responseData as { error?: string })?.error) ||
          (responseData &&
            typeof responseData === "object" &&
            (responseData as { details?: string })?.details) ||
          error.message ||
          "Error initializing payment";
        const isGenericHttpError =
          (error.message?.includes("2xx") ||
            error.message?.includes("status code") ||
            error.message?.includes("5xx") ||
            error.message?.includes("4xx")) &&
          promoCode.trim().length > 0;
        console.error("Edge Function error:", { error, responseData });
        throw new Error(
          error.message?.includes("401") ||
            error.message?.toLowerCase().includes("unauthorized")
            ? t("payments.checkout.sessionExpired")
            : isGenericHttpError ||
                (errorMessage.toLowerCase().includes("invalid") &&
                  errorMessage.toLowerCase().includes("promo"))
              ? t("payments.checkout.promoCodeInvalid")
              : errorMessage,
        );
      }

      const parsed = (
        responseData && typeof responseData === "object" ? responseData : {}
      ) as {
        clientSecret?: string | null;
        subscriptionId?: string;
        error?: string;
        discountApplied?: boolean;
        discountPercent?: number | null;
        originalAmount?: number;
        finalAmount?: number;
      };
      if (parsed?.error) {
        throw new Error(
          parsed.error.toLowerCase().includes("invalid") &&
            parsed.error.toLowerCase().includes("promo")
            ? t("payments.checkout.promoCodeInvalid")
            : parsed.error,
        );
      }
      if (!parsed?.clientSecret) {
        throw new Error("No client secret returned");
      }

      setClientSecret(parsed.clientSecret);
      setSubscriptionId(parsed.subscriptionId ?? null);
      if (parsed.discountApplied) {
        setDiscount({
          discountApplied: true,
          discountPercent: parsed.discountPercent ?? null,
          originalAmount: parsed.originalAmount ?? displayPrice,
          finalAmount: parsed.finalAmount ?? displayPrice,
        });
      } else {
        setDiscount(null);
      }
    } catch (error: any) {
      console.error("Error initializing payment:", error);
      const isNetworkOrCors =
        error?.name === "FunctionsFetchError" ||
        error?.message?.toLowerCase().includes("failed to fetch") ||
        error?.message?.toLowerCase().includes("network");
      const errorMessage = isNetworkOrCors
        ? t(
            "payments.checkout.networkError",
            "Connection error. Ensure the payment service is available and try again.",
          )
        : error.message || t("payments.checkout.initPaymentError");

      toast.error(errorMessage, {
        duration: 6000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const syncSubscriptionStatus = async (): Promise<boolean> => {
    if (!user || !planType || !subscriptionId) {
      return false;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "sync-subscription-status",
        {
          method: "POST",
          body: {
            userId: user.id,
            subscriptionId,
            expectedPlanType: planType,
          },
        },
      );

      if (error) {
        console.warn(
          "[checkout] sync-subscription-status error:",
          error.message,
        );
        return false;
      }

      const parsed = (data && typeof data === "object" ? data : {}) as {
        status?: string;
        planType?: string;
      };

      return parsed.status === "active" && parsed.planType === planType;
    } catch (err) {
      console.warn("[checkout] sync-subscription-status failed:", err);
      return false;
    }
  };

  const handlePaymentSuccess = async () => {
    toast.success(t("payments.checkout.paymentProcessed"));
    setActivating(true);

    // Poll + active sync to avoid dependency on webhook delivery timing.
    const maxAttempts = 10;
    const pollInterval = 2000;
    let activated = false;

    for (let i = 0; i < maxAttempts; i++) {
      await syncSubscriptionStatus();
      await new Promise((r) => setTimeout(r, pollInterval));
      const sub = await getUserSubscription(user!.id);
      if (sub?.plan_type === planType && sub?.status === "active") {
        activated = true;
        break;
      }
    }

    setActivating(false);
    if (!activated) {
      console.warn("[checkout] Subscription not active after polling window");
    }
    if (onSuccess) onSuccess();
    onClose();
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
  };

  if (!planType) return null;

  const planConfig = getPlanConfigSafe(planType);
  if (!planConfig) return null;
  const displayPrice = getDisplayPrice(planConfig.price, billingPeriod);
  const planName = i18n.language === "es" ? planConfig.nameEs : planConfig.name;
  const planFeatures =
    i18n.language === "es" ? planConfig.featuresEs : planConfig.features;
  const isUpgrade =
    currentSubscription &&
    currentSubscription.plan_type !== planType &&
    currentSubscription.status === "active";
  const summaryOriginal = discount?.discountApplied
    ? discount.originalAmount
    : displayPrice;
  const summaryFinal = discount?.discountApplied
    ? discount.finalAmount
    : displayPrice;
  const summaryDiscountAmount = Math.max(summaryOriginal - summaryFinal, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:max-w-md md:max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto"
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-tiber-200 p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-tiber-900">
                      {t("payments.checkout.title")}
                    </h2>
                    <p className="text-sm text-tiber-600 mt-1">
                      {t("payments.checkout.reviewDetails")}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-romance-50 rounded-lg transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-6 h-6 text-tiber-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 space-y-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-tiber-500 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Plan Summary */}
                    <div className="bg-linear-to-br from-tiber-500/10 to-tiber-50 rounded-xl p-6 border border-tiber-500/20">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-linear-to-br from-tiber-500 to-tiber-600 rounded-xl">
                          <Zap className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-tiber-900">
                            {planName}
                          </h3>
                          <p className="text-tiber-600 text-sm">
                            {t("payments.checkout.selectedPlan")}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {planFeatures.map((feature, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <Check className="w-5 h-5 text-atlantis-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-tiber-700">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      {isUpgrade && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">
                                {t("payments.checkout.planUpgrade")}
                              </p>
                              <p className="text-xs text-amber-700 mt-1">
                                {t("payments.checkout.upgradeMessage")}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Payment Security Info */}
                    <div className="bg-romance-50 rounded-lg p-4 border border-tiber-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Shield className="w-5 h-5 text-atlantis-600" />
                        <p className="text-sm font-medium text-tiber-900">
                          {t("payments.checkout.securePayment")}
                        </p>
                      </div>
                      <p className="text-xs text-tiber-600">
                        {t("payments.checkout.securePaymentDescription")}
                      </p>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-white border-2 border-tiber-200 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-tiber-900 mb-4">
                        {t("payments.checkout.orderSummary")}
                      </h3>

                      <div className="mb-4">
                        <label
                          htmlFor="promo-code-sidebar"
                          className="block text-sm font-medium text-tiber-700 mb-1"
                        >
                          {t("payments.checkout.promoCodeLabel")}
                        </label>
                        <input
                          id="promo-code-sidebar"
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          disabled={processing || activating || !!clientSecret}
                          placeholder={t(
                            "payments.checkout.promoCodePlaceholder",
                          )}
                          className="w-full rounded-lg border border-tiber-300 px-3 py-2 text-sm text-tiber-900 focus:outline-none focus:ring-2 focus:ring-tiber-400 disabled:bg-tiber-50 disabled:text-tiber-500"
                        />
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-tiber-600">{planName}</span>
                          <span className="font-semibold text-tiber-900">
                            ${summaryOriginal.toFixed(2)}
                          </span>
                        </div>

                        <div className="border-t border-tiber-200 pt-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-tiber-600">
                              {t("payments.checkout.subtotal")}
                            </span>
                            <span className="text-sm font-medium text-tiber-900">
                              ${summaryOriginal.toFixed(2)}
                            </span>
                          </div>
                          {discount?.discountApplied && (
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-atlantis-700">
                                {t("payments.checkout.discountApplied")}
                                {discount.discountPercent
                                  ? ` (${discount.discountPercent}% OFF)`
                                  : ""}
                              </span>
                              <span className="text-sm font-medium text-atlantis-700">
                                -${summaryDiscountAmount.toFixed(2)}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-tiber-600">
                              {t("payments.checkout.taxes")}
                            </span>
                            <span className="text-sm font-medium text-tiber-900">
                              {t("payments.checkout.taxesCalculated")}
                            </span>
                          </div>
                        </div>

                        <div className="border-t border-tiber-200 pt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-bold text-tiber-900">
                              {discount?.discountApplied
                                ? t("payments.checkout.youPay")
                                : t("payments.checkout.total")}
                            </span>
                            <span className="text-lg font-bold text-tiber-500">
                              ${summaryFinal.toFixed(2)}
                              <span className="text-sm font-normal text-tiber-600 ml-1">
                                /
                                {billingPeriod === "month"
                                  ? t("payments.checkout.month")
                                  : t("payments.checkout.year")}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Stripe Payment Element */}
                      {processing && !clientSecret ? (
                        <div className="text-center py-8">
                          <Loader2 className="w-8 h-8 text-tiber-500 animate-spin mx-auto mb-4" />
                          <p className="text-sm text-tiber-600">
                            {t("payments.checkout.initializingPayment")}
                          </p>
                        </div>
                      ) : activating ? (
                        <div className="text-center py-8">
                          <Loader2 className="w-8 h-8 text-atlantis-500 animate-spin mx-auto mb-4" />
                          <p className="text-sm font-medium text-tiber-700">
                            {t("payments.checkout.activatingSubscription")}
                          </p>
                        </div>
                      ) : clientSecret ? (
                        <div ref={checkoutRef} className="mt-4">
                          <Elements
                            stripe={stripePromise}
                            options={
                              {
                                clientSecret,
                                appearance: {
                                  theme: "stripe",
                                  variables: {
                                    colorPrimary: "#1cb0a9",
                                    colorBackground: "#ffffff",
                                    colorText: "#1f2937",
                                    colorDanger: "#ef4444",
                                    fontFamily: "system-ui, sans-serif",
                                    spacingUnit: "4px",
                                    borderRadius: "8px",
                                  },
                                },
                              } as StripeElementsOptions
                            }
                          >
                            <CheckoutForm
                              clientSecret={clientSecret}
                              onSuccess={handlePaymentSuccess}
                              onError={handlePaymentError}
                            />
                          </Elements>
                        </div>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={initializePayment}
                          disabled={processing || activating}
                          isLoading={processing}
                          className="w-full"
                          size="lg"
                          leftIcon={<CreditCard className="w-5 h-5" />}
                        >
                          {t("payments.checkout.startPayment")}
                        </Button>
                      )}

                      <p className="text-xs text-center text-tiber-500 mt-4">
                        {t("payments.checkout.termsAgreement")}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
